<template>
  <section class="esop-token-form">
    <div
      v-if="esopToken?._id"
      class="overlay"
    >
      <div class="overlay-content">
        <div class="lock-icon">
          <svg width="24" height="24" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
            <rect x="3" y="11" width="18" height="11" rx="2" stroke="currentColor" stroke-width="1.5"/>
            <path d="M7 11V7C7 4.23858 9.23858 2 12 2C14.7614 2 17 4.23858 17 7V11" stroke="currentColor" stroke-width="1.5" stroke-linecap="round"/>
          </svg>
        </div>
        <p class="overlay-text">
          Please
          <a href="mailto:support@getequity.io" class="overlay-link">contact us</a>
          to get edit access for this section.
        </p>
      </div>
    </div>
    
    <div class="form-header">
      <div class="form-icon">
        <svg width="20" height="20" viewBox="0 0 20 20" fill="none" xmlns="http://www.w3.org/2000/svg">
          <path d="M10 1.66667L12.575 6.88334L18.3333 7.725L14.1667 11.7833L15.15 17.5167L10 14.8083L4.85 17.5167L5.83333 11.7833L1.66667 7.725L7.425 6.88334L10 1.66667Z" stroke="currentColor" stroke-width="1.5" stroke-linecap="round" stroke-linejoin="round"/>
        </svg>
      </div>
      <h2 class="form-title">Details of Shares</h2>
    </div>
    
    <form @submit.prevent="checkFormValidation" class="form-content">
      <div class="form-row-double">
        <InputField
          class="field-half"
          :validate="validateForm"
          label="Name of Shares"
          type="text"
          name="name"
          :disabled="esopToken?._id ? true : false"
          :currentValue="organisationDetails.name"
          @input-changed="storeValue"
          @is-valid="handleValidation"
        />
        <InputField
          class="field-half"
          label="Shares Symbol"
          :validate="validateForm"
          :currentValue="token.symbol"
          :disabled="esopToken?._id ? true : false"
          type="text"
          name="symbol"
          @input-changed="storeValue"
          @is-valid="handleValidation"
        />
      </div>

      <div class="form-row">
        <Dropdown
          label="Currency"
          class="field-full"
          :currentValue="token.currency"
          name="currency"
          :options="currencies"
          v-bind="{
            isObject: true,
            objectValue: { label: 'name', value: 'value' },
          }"
          @input-changed="storeValue"
          :validate="validateForm"
          @is-valid="handleValidation"
        />
        <Dropdown
          label="Country"
          class="field-full"
          :currentValue="token.country"
          name="country"
          :options="countryRegionState"
          v-bind="{
            isObject: true,
            objectValue: { label: 'countryName', value: 'countryName' },
          }"
          @input-changed="storeValue"
          :validate="validateForm"
          @is-valid="handleValidation"
        />
      </div>

      <div class="form-row-double">
        <InputField
          label="Total Shares"
          :validate="validateForm"
          class="field-half"
          :currentValue="total_shares"
          :disabled="esopToken?._id ? true : false"
          type="text"
          name="total_shares"
          @input-changed="storeValue"
          @is-valid="handleValidation"
          :validateAsNumber="true"
        />
        <InputField
          label="Amount to be allocated as ESOP"
          :validate="validateForm"
          class="field-half"
          type="text"
          name="supply"
          :disabled="esopToken?._id ? true : false"
          :maxValue="valuation"
          maxValueErrorMessage="Allocated shares can't be greater than total shares"
          :currentValue="supply"
          :validateAsNumber="true"
          @input-changed="storeValue"
          @is-valid="handleValidation"
        />
      </div>

      <div class="form-row">
        <InputField
          label="Shares Market Price (USD)"
          :validate="validateForm"
          type="text"
          name="marketPrice"
          :disabled="esopToken?._id ? true : false"
          :zeroValue="true"
          zeroValueErrorMessage="Market price can't be 0"
          :currentValue="marketPrice"
          :validateAsNumber="true"
          @input-changed="storeValue"
          @is-valid="handleValidation"
        />
        <p class="valuation-text">
          Company Valuation:
          <span class="valuation-amount">{{ formatAmountToDollar(valuation, 2, "USD") }}</span>
        </p>
      </div>

      <div class="form-row">
        <InputField
          label="Name of Grantor (Eg: Company Name, CEO)"
          :validate="validateForm"
          class="field-full"
          :currentValue="token.grantor"
          :disabled="esopToken?._id ? true : false"
          type="text"
          name="grantor"
          @input-changed="storeValue"
          @is-valid="handleValidation"
        />
      </div>

      <div class="form-footer">
        <div class="checkbox-row">
          <Checkbox
            title="Enable Trade"
            label="Enable Trade"
            value="secondaries"
            @checked="storeValue"
            :disabled="esopToken?._id ? true : false"
            :checked="token.secondaries"
          />
          <Tooltip
            :text="'Enabling trade means that your employees can buy or sell allocated shares amongst themselves.'"
          />
        </div>
        <div v-if="!esopToken?._id" class="submit-btn-wrapper">
          <CustomButton
            :title="buttonText"
            :loading="documentInfoLoading"
          />
        </div>
      </div>
    </form>

    <SmallModal
      v-if="showPreviewModal"
      @close="showPreviewModal = !showPreviewModal"
    >
      <template v-slot:content>
        <div
          class="tw-flex tw-justify-between tw-items-center tw-border-b tw-pb-4"
        >
          <h3 class="tw-font-bold">Shares Summary</h3>
          <img
            class="tw-h-12 tw-rounded"
            :src="assetDetails.image"
            alt="organisation"
          />
        </div>
        <div
          v-for="(detail, index) in previewDetails"
          :key="index"
          class="tw-flex tw-justify-between tw-items-center tw-border-b tw-py-4"
        >
          <h3 class="tw-text-gray">{{ detail.key }}</h3>
          <h3>{{ detail.value }}</h3>
        </div>
        <CustomButton
          class="tw-mt-12"
          title="Continue"
          @click="createNewEsopToken"
        />
      </template>
    </SmallModal>
  </section>
</template>

<script>
import InputField from "@/components/general/InputField";
import SmallModal from "@/components/general/SmallModal";
import { formatAmountToDollar, convertToLocale } from "@/utils/helpers.js";
import Checkbox from "@/components/general/Checkbox";
import Tooltip from "@/components/general/Tooltip";
import CustomButton from "@/components/general/BtnComponent";
import { mapActions, mapState, mapGetters } from "vuex";
import countryRegionState from "@/utils/js/countryRegionData";
import Dropdown from "@/components/general/Dropdown";

export default {
  name: "EsopTokenForm",
  data() {
    return {
      countryRegionState: countryRegionState,
      documentInfoLoading: false,
      showPreviewModal: false,
      suggestedSymbolName: "",
      tokenNameItems: [],
      validateForm: false,
      token: {
        name: "",
        symbol: "",
        valuation: null,
        marketPrice: null,
        grantor: null,
        secondaries: false,
        esop_supply: null,
        total_shares: null,
        currency: null,
        country: null,
      },
      previewDetails: [],
      total_shares: null,
      supply: null,
      marketPrice: null,
      validationResult: [],
      currencies: [
        { name: "United States Dollars", value: "USD" },
        {
          name: "Nigerian Naira",
          value: "NGN",
        },
        {
          name: "Kenyan Shillings",
          value: "KES",
        },
      ],
    };
  },
  emits: ["next-page"],
  props: {
    prefill: {
      type: Boolean,
      required: false,
      default: () => false,
    },
    buttonText: {
      type: String,
      required: true,
    },
  },
  components: {
    InputField,
    SmallModal,
    Checkbox,
    Tooltip,
    CustomButton,
    Dropdown
  },
  watch: {
    "token.name"() {
      this.createTokenSymbol();
    },
    suggestedSymbolName: "checkSuggestedSymbolLength",
    marketPriceFormatted(val) {
      this.marketPrice = val;
    },
    supplyFormatted(val) {
      this.supply = val;
    },
    totalSharesFormatted(val) {
      this.total_shares = val;
    },
    organisationDetails(val) {
      this.token.name = val.name;
    },
    validationResult: {
      deep: true,
      handler(val) {
        if (
          val.length === Object.keys(this.token).length - 2 &&
          !val.includes(false)
        ) {
          this.setPreviewDetails();
        }
      },
    },
    prefill(val) {
      if (val) {
        this.fillForm();
      }
    },
    valuation(val) {
      this.token.valuation = val;
    },
  },
  mounted() {
    if (this.prefill) {
      this.fillForm();
    }
    this.token.name = this.organisationDetails.name;
    this.getAsset(this.organisationDetails.asset);
  },
  computed: {
    ...mapGetters("organisationModule", ["organisationDetails"]),
    ...mapGetters("Esop", ["esopToken"]),
    ...mapState({
      assetDetails: (state) => state.assetModule.assetDetails,
    }),
    marketPriceFormatted() {
      let marketPriceFormatted = null;
      if (this.marketPrice) {
        const marketPrice = Number(this.marketPrice.split(",").join(""));
        this.token.marketPrice = marketPrice;
        marketPriceFormatted = this.convertToLocale(marketPrice);
      }
      return marketPriceFormatted;
    },
    supplyFormatted() {
      let supplyFormatted = null;
      if (this.supply) {
        const supply = Number(this.supply.split(",").join(""));
        this.token.esop_supply = supply;
        supplyFormatted = this.convertToLocale(supply);
      }
      return supplyFormatted;
    },
    totalSharesFormatted() {
      let totalSharesFormatted = null;
      if (this.total_shares) {
        const total_shares = Number(this.total_shares.split(",").join(""));
        this.token.total_shares = total_shares;
        totalSharesFormatted = this.convertToLocale(total_shares);
      }
      return totalSharesFormatted;
    },
    valuation() {
      return this.token?.marketPrice * this.token?.total_shares;
    },
  },
  methods: {
    ...mapActions("Esop", ["createEsopToken", "updateEsopToken"]),
    ...mapActions("assetModule", ["getAsset", "verifyTokenSymbol"]),
    ...mapActions(["showToast"]),
    convertToLocale,
    formatAmountToDollar,
    createTokenSymbol() {
      this.suggestedSymbolName = "";
      const tokenName = this.token.name;
      let suggestedSymbolName = this.suggestedSymbolName;
      const tokenNameItems = tokenName.split(" ");
      tokenNameItems.map((item) => {
        if (suggestedSymbolName.length < 6) {
          suggestedSymbolName += item.charAt(0).toUpperCase();
        }
      });
      this.tokenNameItems = tokenNameItems;
      this.token.symbol = suggestedSymbolName;
      this.suggestedSymbolName = suggestedSymbolName;
      this.checkSuggestedSymbolLength();
    },
    fillForm() {
      this.token.name = this.esopToken.name;
      this.token.symbol = this.esopToken.symbol;
      this.marketPrice = String(
        this.esopToken.valuation / this.esopToken.total_supply
      );
      this.token.grantor = this.esopToken.employee_benefit_grantor;
      this.token.secondaries = this.esopToken.secondaries;
      this.supply = String(this.esopToken.supply);
      this.total_shares = String(this.esopToken.total_supply);
    },
    checkSuggestedSymbolLength() {
      let suggestedSymbolName = this.suggestedSymbolName;
      const tokenNameItems = this.tokenNameItems;
      const suggestedSymbolNameLength = suggestedSymbolName.length.toString();
      const mergedTokenName = tokenNameItems.join("");
      const mergedTokenNameLength = mergedTokenName.length;
      const secondLetter = tokenNameItems[0].charAt(1);
      const midPoint = Math.round(mergedTokenNameLength / 2);
      const middleLetter = mergedTokenName.charAt(midPoint);
      const endPoint = mergedTokenNameLength - 1;
      const lastLetter = mergedTokenName.charAt(endPoint);
      switch (suggestedSymbolNameLength) {
        case "1":
          suggestedSymbolName += secondLetter.toUpperCase();
          break;
        case "2":
          suggestedSymbolName += middleLetter.toUpperCase();
          break;
        case "3":
          suggestedSymbolName += lastLetter.toUpperCase();
          break;
      }
      this.suggestedSymbolName = suggestedSymbolName;
      this.token.symbol = suggestedSymbolName + "-ESOP";
    },
    setPreviewDetails() {
      this.previewDetails = [
        { key: "Asset Name", value: this.token.name },
        { key: "Asset Symbol", value: this.token.symbol },
        {
          key: "Market price",
          value: formatAmountToDollar(this.marketPrice, 2, "USD"),
        },
        {
          key: "No of Shares",
          value: convertToLocale(this.token.total_shares),
        },
        {
          key: "Allocated Shares",
          value: convertToLocale(this.token.esop_supply),
        },
        {
          key: "Company valuation",
          value: formatAmountToDollar(this.token.valuation, 2, "USD"),
        },
        { key: "Grantor name", value: this.token.grantor },
      ];
      this.showPreviewModal = true;
    },
    storeValue(object) {
      if (
        object.name !== "valuation" &&
        object.name !== "marketPrice" &&
        object.name !== "total_shares" &&
        object.name !== "supply"
      ) {
        this.token[object.name] = object.value;
        this.verifyTokenSymbol(this.token.symbol);
      } else {
        this[object.name] = object.value;
      }
    },
    async createNewEsopToken() {
      try {
        let response;
        if (!this.esopToken?._id) {
          this.token.asset = this.organisationDetails.asset;
          delete this.token.marketPrice;
          response = await this.createEsopToken(this.token);
        } else {
          const payload = {
            name: this.token.name,
            valuation: this.token.valuation,
            secondaries: this.token.secondaries,
            total_shares: this.token.total_shares,
            country: this.token.country,
            currency: this.token.currency,
          };
          response = await this.updateEsopToken(payload);
        }
        const { message } = response.data;
        this.showToast({
          description: message,
          display: true,
          type: "success",
        });
        this.$emit("next-page", "EsopTokenForm");
      } catch (error) {
        return error;
      }
    },
    checkFormValidation() {
      this.validationResult = [];
      this.validateForm = true;
      setTimeout(() => {
        this.validateForm = false;
      }, 1000);
    },
    handleValidation(value) {
      this.validationResult.push(value);
    },
  },
};
</script>

<style lang="scss" scoped>
.esop-token-form {
  position: relative;
  background: #FFFFFF;
  border: 1px solid #E2E8F0;
  border-radius: 12px;
  padding: 24px 20px;

  @media screen and (min-width: 768px) {
    padding: 32px;
  }
}

.overlay {
  position: absolute;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background: rgba(15, 23, 42, 0.85);
  border-radius: 12px;
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 10;
}

.overlay-content {
  text-align: center;
  padding: 24px;
}

.lock-icon {
  width: 48px;
  height: 48px;
  background: rgba(255, 255, 255, 0.1);
  border-radius: 12px;
  display: flex;
  align-items: center;
  justify-content: center;
  margin: 0 auto 16px;
  color: #FFFFFF;
}

.overlay-text {
  font-family: 'DM Sans', sans-serif;
  font-size: 15px;
  font-weight: 400;
  color: #FFFFFF;
  line-height: 1.6;
  margin: 0;
}

.overlay-link {
  color: #4D84FF;
  text-decoration: underline;

  &:hover {
    color: #3B6FE6;
  }
}

.form-header {
  display: flex;
  align-items: center;
  gap: 12px;
  margin-bottom: 28px;
}

.form-icon {
  width: 36px;
  height: 36px;
  background: #EEF2FF;
  border-radius: 8px;
  display: flex;
  align-items: center;
  justify-content: center;
  color: #4D84FF;
}

.form-title {
  font-family: 'DM Sans', sans-serif;
  font-size: 18px;
  font-weight: 500;
  color: #0F172A;
  margin: 0;
}

.form-content {
  display: flex;
  flex-direction: column;
  gap: 20px;
}

.form-row {
  display: flex;
  flex-direction: column;
  gap: 16px;
}

.form-row-double {
  display: flex;
  flex-direction: column;
  gap: 16px;

  @media screen and (min-width: 768px) {
    flex-direction: row;
    gap: 16px;
  }
}

.field-half {
  width: 100%;

  @media screen and (min-width: 768px) {
    width: 50%;
  }
}

.field-full {
  width: 100%;
}

.valuation-text {
  font-family: 'DM Sans', sans-serif;
  font-size: 13px;
  font-weight: 400;
  color: #64748B;
  margin: 4px 0 0 0;
}

.valuation-amount {
  font-weight: 500;
  color: #0F172A;
}

.form-footer {
  display: flex;
  flex-direction: column;
  gap: 20px;
  margin-top: 8px;

  @media screen and (min-width: 640px) {
    flex-direction: row;
    align-items: center;
    justify-content: space-between;
  }
}

.checkbox-row {
  display: flex;
  align-items: center;
  gap: 8px;
}

.submit-btn-wrapper {
  width: 100%;

  @media screen and (min-width: 640px) {
    width: auto;
    min-width: 160px;
  }
}
</style>
